import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHeadphones, FiTrendingUp, FiPlay, FiPause } = FiIcons;

const Hero = () => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Listen for the custom play event from Header
  useEffect(() => {
    const handleTriggerPlay = () => {
      // Automatically start playing audio when triggered from header
      if (!isPlaying) {
        toggleAudio();
      }
    };

    window.addEventListener('triggerHeroPlay', handleTriggerPlay);
    
    return () => {
      window.removeEventListener('triggerHeroPlay', handleTriggerPlay);
    };
  }, [isPlaying]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleAudio = async () => {
    if (!audioRef.current) {
      console.error('Audio reference not available');
      return;
    }

    try {
      if (isPlaying) {
        // Pause the audio
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        // Play the audio
        if (audioRef.current.ended) {
          audioRef.current.currentTime = 0;
        }
        
        // Ensure audio is loaded before playing
        if (audioRef.current.readyState < 2) {
          audioRef.current.load();
          await new Promise((resolve) => {
            audioRef.current.addEventListener('canplay', resolve, { once: true });
          });
        }
        
        await audioRef.current.play();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error('Audio playback failed:', error);
      setIsPlaying(false);
      
      // Try to reload and play again if it was a play attempt
      if (!isPlaying) {
        try {
          audioRef.current.load();
          await new Promise((resolve) => setTimeout(resolve, 100));
          await audioRef.current.play();
          setIsPlaying(true);
        } catch (retryError) {
          console.error('Retry audio playback failed:', retryError);
        }
      }
    }
  };

  const handleAudioPlay = () => {
    setIsPlaying(true);
  };

  const handleAudioPause = () => {
    setIsPlaying(false);
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const handleAudioError = () => {
    setIsPlaying(false);
    console.error('Audio loading error');
  };

  return (
    <section id="hero" className="relative min-h-[120vh] flex items-center justify-center overflow-hidden pt-32 pb-20">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-dental-blue via-ai-teal to-dental-blue opacity-90"></div>
      
      {/* Animated Background Elements - Increased size and coverage */}
      <div className="absolute inset-0 w-full h-full">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-3 h-3 bg-clean-white rounded-full opacity-20"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -40, 0],
              opacity: [0.2, 0.6, 0.2],
            }}
            transition={{
              duration: 4 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 3,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="space-y-8"
        >
          {/* Main Headline - Updated text */}
          <motion.h1
            className="text-4xl md:text-6xl font-bold text-clean-white leading-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            EngageDent Ai grows{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-growth-green to-energy-coral">
              your practice
            </span>
            {' '}quickly by optimizing your marketing, increasing patient bookings, and verifying insurance.
          </motion.h1>

          {/* Tagline - Now at the bottom */}
          <motion.p
            className="text-2xl md:text-3xl text-clean-white opacity-95 max-w-4xl mx-auto leading-relaxed font-semibold"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            More patients. Less friction.<br />
            Bigger bottom lines — with AI.
          </motion.p>

          {/* Video Embed */}
          <motion.div
            className="mt-8 mb-8 flex justify-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <div className="relative w-full max-w-4xl aspect-video rounded-xl overflow-hidden shadow-2xl">
              <video
                className="w-full h-full object-cover"
                controls
                preload="metadata"
                poster=""
              >
                <source 
                  src="https://mzunalhoitapgthoksjn.supabase.co/storage/v1/object/public/site-assets/Copy%20of%20Transform%20Your%20Dental%20Practice%20with%20EngageDent%20AI.mp4" 
                  type="video/mp4" 
                />
                Your browser does not support the video tag.
              </video>
            </div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row gap-6 justify-center items-center mt-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <motion.button
              onClick={() => scrollToSection('demo')}
              className="bg-energy-coral text-clean-white px-8 py-4 rounded-xl font-semibold text-lg flex items-center space-x-3 hover:bg-opacity-90 transition-all duration-200 shadow-2xl"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <SafeIcon icon={FiHeadphones} className="text-xl" />
              <span>Listen to Donna</span>
            </motion.button>

            <motion.button
              onClick={() => scrollToSection('roi')}
              className="bg-clean-white text-dental-blue px-8 py-4 rounded-xl font-semibold text-lg flex items-center space-x-3 hover:bg-opacity-90 transition-all duration-200 shadow-2xl"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <SafeIcon icon={FiTrendingUp} className="text-xl" />
              <span>See Your ROI</span>
            </motion.button>
          </motion.div>

          {/* Floating AI Visual with Play/Pause - This is the main play button */}
          <motion.div
            className="mt-12 flex justify-center"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.8 }}
          >
            <motion.div
              className="relative cursor-pointer"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
              onClick={toggleAudio}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-play-button="true"
            >
              <div className="w-32 h-32 bg-gradient-to-br from-growth-green to-ai-teal rounded-full flex items-center justify-center shadow-2xl">
                <SafeIcon 
                  icon={isPlaying ? FiPause : FiPlay} 
                  className="text-clean-white text-4xl ml-1" 
                />
              </div>
              {/* Pulse rings - only show when playing */}
              {isPlaying && [...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute inset-0 rounded-full border-2 border-clean-white"
                  animate={{
                    scale: [1, 1.5, 2],
                    opacity: [0.6, 0.3, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.6,
                  }}
                />
              ))}
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        src="https://mzunalhoitapgthoksjn.supabase.co/storage/v1/object/public/site-assets/riverside_ben_linkedin_website.mp3"
        preload="metadata"
        onPlay={handleAudioPlay}
        onPause={handleAudioPause}
        onEnded={handleAudioEnded}
        onError={handleAudioError}
      />
    </section>
  );
};

export default Hero;